import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import {
  Button,
  DisplayText,
  Form,
  theme,
  TYPE_CONFIG,
  useResponsive,
} from '@sg/garnish';

import { useForgotToScanForm } from '../../hooks';
import { type ForgotToScanInput } from '../../utils';
import { ForgotToScanFaqLink } from '../ForgotToScanFaqLink';

export const ForgotToScanForm = (props: ForgotToScanFormProps) => {
  const { title, faqLink, isSubmittingForm, onSubmitForm, onFaqPressed } =
    props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint, match } = useResponsive();

  const { form, submitForm } = useForgotToScanForm({
    onSubmit: onSubmitForm,
  });

  const { control, formState, reset: resetForm } = form;
  const { errors, isValid: canSubmit } = formState;

  // Reset form on focus.
  useFocusEffect(resetForm);

  return (
    <View style={match([styles.containerXS, styles.containerSM])}>
      <View style={styles.content}>
        {currentBreakpoint.isXS ? (
          <DisplayText style={styles.title} sizeMatch={['32']}>
            {title}
          </DisplayText>
        ) : null}

        <Form.TextFieldZipCodeWithBackground
          hideNoticeSpace
          hideSubmitButton
          name="zipCode"
          control={control}
          rules={{ required: true }}
          notice={errors.zipCode?.message}
          labelStyle={styles.upperCaseLabel}
          label={formatMessage(messages.zipCode)}
          accessibilityLabel={formatMessage(messages.zipCode)}
          placeholder={formatMessage(messages.zipCodePlaceholder)}
        />

        <Form.TextFieldWithBackground
          hideNoticeSpace
          hideSubmitButton
          name="orderId"
          control={control}
          rules={{ required: true }}
          notice={errors.orderId?.message}
          labelStyle={styles.upperCaseLabel}
          label={formatMessage(messages.orderId)}
          accessibilityLabel={formatMessage(messages.orderId)}
          placeholder={formatMessage(messages.orderIdPlaceholder)}
        />

        <Form.TextFieldDateWithBackground
          hideNoticeSpace
          hideSubmitButton
          name="purchaseDate"
          control={control}
          rules={{ required: true }}
          notice={errors.purchaseDate?.message}
          labelStyle={styles.upperCaseLabel}
          label={formatMessage(messages.purchaseDate)}
          accessibilityLabel={formatMessage(messages.purchaseDate)}
          placeholder={formatMessage(messages.purchaseDatePlaceholder)}
        />

        <Form.TextFieldCurrencyWithBackground
          hideNoticeSpace
          hideSubmitButton
          name="orderTotal"
          control={control}
          rules={{ required: true }}
          labelStyle={styles.upperCaseLabel}
          label={formatMessage(messages.orderTotal)}
          accessibilityLabel={formatMessage(messages.orderTotal)}
          placeholder={formatMessage(messages.orderTotalPlaceholder)}
        />
      </View>

      <View style={match([styles.footerXS, styles.footerSM])}>
        <Button
          size="large-wide"
          style={match([styles.ctaXS, undefined])}
          accessibilityLabel={formatMessage(messages.ctaA11yLabel)}
          isLoading={isSubmittingForm}
          disabled={!canSubmit}
          onPress={submitForm}
        >
          <FormattedMessage {...messages.cta} />
        </Button>

        {currentBreakpoint.isXS ? null : (
          <ForgotToScanFaqLink link={faqLink} onPress={onFaqPressed} />
        )}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flex: 1,
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingBottom: theme.spacing['10'],
    gap: theme.spacing['4'],
  },
  containerSM: {
    flex: 1,
    padding: theme.spacing['4'],
    paddingBottom: theme.spacing['10'],
    gap: theme.spacing['10'],
  },
  content: {
    gap: theme.spacing['4'],
  },
  title: {
    marginBottom: theme.spacing['2'],
    color: theme.colors.KALE,
  },
  footerXS: {
    justifyContent: 'space-between',
  },
  footerSM: {
    flex: 1,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ctaXS: {
    width: '100%',
  },
  upperCaseLabel: {
    ...TYPE_CONFIG.LABEL['10'],
    textTransform: 'uppercase',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  cta: {
    defaultMessage: 'Submit',
    description: 'Forgot To Scan | Form | Submit',
  },
  ctaA11yLabel: {
    defaultMessage: 'Requests points for this order',
    description: 'Forgot To Scan | Form | Submit',
  },
  zipCode: {
    defaultMessage: 'Zip code',
    description: 'Forgot To Scan | Form | Zip code',
  },
  zipCodePlaceholder: {
    defaultMessage: 'e.g. 00000',
    description: 'Forgot To Scan | Form | Zip code placeholder',
  },
  orderId: {
    defaultMessage: 'Order ID',
    description: 'Forgot To Scan | Form | Order ID',
  },
  orderIdPlaceholder: {
    defaultMessage: 'e.g. 9597283426',
    description: 'Forgot To Scan | Form | Order ID placeholder',
  },
  purchaseDate: {
    defaultMessage: 'Purchase date',
    description: 'Forgot To Scan | Form | Purchase date',
  },
  purchaseDatePlaceholder: {
    defaultMessage: 'MM/DD/YYYY',
    description: 'Forgot To Scan | Form | Purchase date placeholder',
  },
  orderTotal: {
    defaultMessage: 'Order total',
    description: 'Forgot To Scan | Form | Order total',
  },
  orderTotalPlaceholder: {
    defaultMessage: 'e.g. $100.00',
    description: 'Forgot To Scan | Form | Order total placeholder',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ForgotToScanFormProps = {
  title: string;
  faqLink: string;
  isSubmittingForm: boolean;
  onFaqPressed: () => void;
  onSubmitForm: (payload: ForgotToScanInput) => void;
};
